// Backup service — API_INTEGRATION.md §3.27
// Tenant data backup management. Traceability: FR-BACK-001 to FR-BACK-010

import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type BackupStatus = 'QUEUED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
export type BackupType = 'SCHEDULED' | 'ON_DEMAND';

export interface Backup {
  id: string;
  tenant_id: string;
  type: BackupType;
  status: BackupStatus;
  file_size_bytes: number | null;
  download_url: string | null;
  download_expires_at: string | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface BackupListParams {
  page?: number;
  limit?: number;
  status?: string;
}

export interface BackupListMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  // Tenant-wide aggregates (whole history, not the current page). Used for the
  // "Storage Used" and "Last Backup" KPI cards.
  total_size_bytes: number;
  last_completed_at: string | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const backupService = {
  async list(params: BackupListParams = {}): Promise<{
    data: Backup[];
    meta: BackupListMeta;
  }> {
    const q = buildQuery({ page: params.page, limit: params.limit ?? 20, status: params.status });
    const res = await apiClientPaginated<Backup[]>(`/backups${q}`);
    return { data: res.data, meta: res.meta as BackupListMeta };
  },

  async getById(id: string): Promise<Backup> {
    return apiClient<Backup>(`/backups/${id}`);
  },

  async trigger(): Promise<Backup> {
    return apiClient<Backup>('/backups', { method: 'POST' });
  },

  async getDownloadUrl(id: string): Promise<{ download_url: string }> {
    return apiClient<{ download_url: string }>(`/backups/${id}/download`);
  },
};
